import styles from './PriceHistory.module.scss'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Banner, Button, Drawer, Heading, Toggle } from '@boltenergy-be/design-system'
import GranularityNavigation from 'components/charts/granularity-navigation/GranularityNavigation.tsx'
import { GranularityNavigationForm, GranularityOptions } from 'components/charts/granularity-navigation/types.ts'
import PriceHistoryChart from 'components/charts/price-history/PriceHistoryChart.tsx'
import EmptyState from 'components/return-later/EmptyState.tsx'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import { ISO_DATETIME_FORMAT } from 'constants/constants.ts'
import { useStoreSelector } from 'hooks/store.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { memoizedGenerateGranularityNavigationItems, memoizedGenerateGranularityRange } from 'pages/app/consumption/utils.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { useGetPriceHistoryQuery } from 'store/queries/bolt-api/metrics'
import { FullPeriodPrice } from 'components/charts/price-history/types.ts'
import { PRICE_HISTORY_QUERY_TYPE, PRICE_HISTORY_RESPONSE_KEY } from './constants.ts'
import { generateFullPeriodPrices } from './utils.ts'
import { ENERGY_TYPES, EnergyType } from '@boltenergy-be/shared'
import { Granularity } from 'types/contracts.ts'
import { isActiveContract } from 'utils/contracts.ts'
import parse from 'html-react-parser'

const PriceHistory = () => {
  // Redux
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)

  // Access rights
  const { historic } = useGetAccessRight(ACCESS_RIGHT_KEYS.marketPrices)

  // Window size
  const { isTablet } = useWindowSize()

  // i18n
  const { t } = useTranslation(['consumption', 'common', 'errors'])

  // Local state
  const [activeEnergyType, setActiveEnergyType] = useState<EnergyType>(ENERGY_TYPES.electricity)
  const [showCalculationInfoModal, setShowCalculationInfoModal] = useState<boolean>(false)

  // Navigation items (market prices are known per quarter hour for every contract, unlike the volumes)
  const granularities: GranularityOptions = memoizedGenerateGranularityNavigationItems({
    contract: activeEnergyType === ENERGY_TYPES.gas && !!gas ? gas : electricity
  })

  // React hook form
  const hookForm = useForm<GranularityNavigationForm>({
    defaultValues: {
      view: Granularity.MONTH,
      period: granularities[Granularity.MONTH].periods?.find((period) => period.defaultSelected)?.value
    }
  })
  const { control, setValue } = hookForm
  const watchView = useWatch({ control, name: 'view' })
  const watchPeriod = useWatch({ control, name: 'period' })

  // Reset period when there is no matching period for the selected granularity view
  useEffect(() => {
    const currentGranularity = granularities[watchView]
    if (!!currentGranularity?.periods && !currentGranularity.periods.some((period) => period.value === watchPeriod)) {
      const defaultPeriod =
        currentGranularity.periods.find((period) => period.defaultSelected)?.value || currentGranularity.periods[0].value
      hookForm.setValue('period', defaultPeriod)
    }
  }, [granularities, hookForm, watchPeriod, watchView])

  // Redux Queries
  const range = memoizedGenerateGranularityRange({ view: watchView, period: watchPeriod })
  // The API only receives the date part of until & treats it as an exclusive boundary, so shift it a day to include the last period
  const until = range?.until.startOf('day').add(1, 'day')
  const { currentData, isLoading, isFetching, isError, error } = useGetPriceHistoryQuery(
    {
      granularity: range?.granularity || Granularity.MONTH,
      from: range?.from.startOf('day').format(ISO_DATETIME_FORMAT) || '',
      until: until?.format(ISO_DATETIME_FORMAT) || '',
      unit: 'kWh',
      type: PRICE_HISTORY_QUERY_TYPE[activeEnergyType]
    },
    { skip: !historic.canAccess || !range }
  )

  /**
   * Right after switching period the query for the new period is not fetching yet, while currentData (unlike data) is
   * already empty. Both states mean the prices of the selected period are still on their way
   */
  const isLoadingPrices = isFetching || !currentData

  // Memoized prices for the active energy type, covering every period of the selected view
  const prices = useMemo<FullPeriodPrice[]>(
    () =>
      range
        ? generateFullPeriodPrices({
            ...range,
            prices: currentData?.priceHistory?.[PRICE_HISTORY_RESPONSE_KEY[activeEnergyType]] || []
          })
        : [],
    [currentData, activeEnergyType, range]
  )
  const hasPrices = prices.some(({ price }) => price !== null)

  return (
    <>
      <section className={styles['price-history']}>
        {!historic.canAccess ? (
          <EmptyState
            icon="cross"
            title={t(($) => $.emptyState.noData.title, {
              dataTranslation: t(($) => $.priceHistory.data),
              interpolation: { escapeValue: false },
              ns: 'common'
            })}
            description={t(($) => $.priceHistory.noAccess)}
          />
        ) : (
          <div className={styles['prices-wrapper']}>
            {isLoading ? (
              <LoadingSkeleton className="mb-auto mt-none">
                <LoadingSkeleton.Rectangle height={98} />
                <LoadingSkeleton.Rectangle aspectRatio="3/1" />
                <div className="flex-col gap-200">
                  <LoadingSkeleton.Rectangle height={20} />
                  <LoadingSkeleton.Rectangle height={20} />
                </div>
              </LoadingSkeleton>
            ) : (
              <>
                {/* FILTERING */}
                <header className={styles.header}>
                  <FormProvider {...hookForm}>
                    <GranularityNavigation
                      alwaysShowGranularitySelector={!!electricity && !!gas && electricity.detail.dynamicTariff}
                      allowSwitchAddress={false}
                      labelContext="prices"
                      {...{ granularities }}
                    >
                      {gas && isActiveContract(gas) && (
                        <Toggle
                          active={activeEnergyType}
                          onClick={(type) => {
                            setActiveEnergyType(type)
                            if (type === ENERGY_TYPES.gas) {
                              setValue('view', Granularity.MONTH)
                              setValue('period', granularities[Granularity.MONTH].periods?.find((p) => p.defaultSelected)?.value ?? '')
                            }
                          }}
                          className={styles.toggle}
                          radius={isTablet ? 'full' : 'md'}
                          options={[
                            {
                              label: t(($) => $.electricity, {
                                ns: 'common'
                              }),
                              value: ENERGY_TYPES.electricity
                            },
                            {
                              label: t(($) => $.gas, {
                                ns: 'common'
                              }),
                              value: ENERGY_TYPES.gas
                            }
                          ]}
                        />
                      )}
                    </GranularityNavigation>
                  </FormProvider>
                </header>

                {/* PRICE HISTORY CHART */}
                {isError || error ? (
                  <Banner type="warning">
                    {t(($) => $.tryAgain, {
                      ns: 'errors'
                    })}
                  </Banner>
                ) : isLoadingPrices || hasPrices ? (
                  <PriceHistoryChart
                    data={prices}
                    energyType={activeEnergyType}
                    granularity={range?.granularity || Granularity.MONTH}
                    isFetching={isLoadingPrices}
                    {...{ isTablet }}
                  />
                ) : (
                  <Banner type="informative">{t(($) => $.priceHistory.noData)}</Banner>
                )}

                <Button
                  representation="link"
                  className={styles['calculations-trigger']}
                  type="button"
                  onClick={() => setShowCalculationInfoModal(true)}
                >
                  {t(($) => $.priceHistory.calculations.title)}
                </Button>
              </>
            )}
          </div>
        )}
      </section>

      <Drawer
        isOpen={showCalculationInfoModal}
        onRequestClose={() => setShowCalculationInfoModal(false)}
        title={t(($) => $.priceHistory.calculations.title)}
      >
        <p>{t(($) => $.priceHistory.calculations.p1)}</p>
        <p>{parse(t(($) => $.priceHistory.calculations.p2))}</p>
        <p>{parse(t(($) => $.priceHistory.calculations.p3))}</p>

        <div>
          <Heading as="h3" variant="h5" className="mb-200">
            {t(($) => $.priceHistory.calculations.subtitle)}
          </Heading>
          <p>{t(($) => $.priceHistory.calculations.injection)}</p>
        </div>
      </Drawer>
    </>
  )
}

export default PriceHistory
